/**
 * Rack Viewer Component - Visual Rack Representation
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ServerIcon, BoltIcon, Square3Stack3DIcon, ArrowsRightLeftIcon,
  Squares2X2Icon, GlobeAltIcon, CircleStackIcon, ComputerDesktopIcon,
  PrinterIcon, ShieldCheckIcon, PhoneIcon, ArchiveBoxIcon, VideoCameraIcon,
} from '@heroicons/react/24/solid';
// El icono del rack es el MISMO del listado de racks (variante outline), no un
// emoji: así la cabecera del visor concuerda con el resto de la aplicación.
import { RectangleStackIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import PortMapModal from './PortMapModal';
import ComponentsModal from './ComponentsModal';
import RackElementQuickCreate from './RackElementQuickCreate';
import PromoteRackDialog from './PromoteRackDialog';
import useTouchMode from '../hooks/useTouchMode';
import {
  canPlaceAt as canPlaceAtPure,
  elementUs,
  rangeOf,
  rangesOverlap as rangesOverlapPure,
} from '../utils/rackPlacement';
import { CABLE_COLORS, CABLE_TYPES, colorHex } from './wiring/wiringGeometry';
import { tracePath } from './wiring/cablePath';
import './RackViewer.css';
import './RackViewerTouch.css';

const TYPE_META = {
  server:      { label: 'Servidor',       Icon: ServerIcon },
  switch:      { label: 'Switch',         Icon: ArrowsRightLeftIcon },
  pdu:         { label: 'PDU',            Icon: BoltIcon },
  ups:         { label: 'SAI / UPS',      Icon: BoltIcon },
  panel:       { label: 'Patch Panel',    Icon: Squares2X2Icon },
  chassis:     { label: 'Chasis',         Icon: Square3Stack3DIcon },
  router:      { label: 'Router',         Icon: GlobeAltIcon },
  storage:     { label: 'Almacenamiento', Icon: CircleStackIcon },
  workstation: { label: 'Workstation',    Icon: ComputerDesktopIcon },
  printer:     { label: 'Impresora',      Icon: PrinterIcon },
  firewall:    { label: 'Firewall',       Icon: ShieldCheckIcon },
  phone:       { label: 'Teléfono IP',    Icon: PhoneIcon },
  nvr:         { label: 'Grabador NVR',   Icon: VideoCameraIcon },
  tray:        { label: 'Bandeja',        Icon: ArchiveBoxIcon },
};

const FILLER_META = {
  tapa_ciega: { label: 'Tapa Ciega', color: '#94a3b8', icon: '▬' },
  pasa_hilos: { label: 'Pasa Hilos', color: '#64748b', icon: '⟺' },
  bandeja:    { label: 'Bandeja',    color: '#78716c', icon: '▭' },
};

const INSTALL_LABEL = {
  planned: 'Planificado',
  mounted: 'Montado',
  verified: 'Validado',
};

const MISSING_LABEL = {
  name: 'nombre',
  rack_position: 'posición en el rack',
  serial_number: 'nº de serie',
  ip_address: 'IP',
};

function PortDot({ summary }) {
  const bg = summary.connected === summary.total && summary.total > 0
    ? '#22c55e' : summary.connected > 0 ? '#f59e0b' : '#e2e8f0';
  return (
    <div className="rack-item-ports">
      <span className="port-count-dot" style={{ background: bg }} />
      {summary.connected}/{summary.total}
    </div>
  );
}

function RackItemContent({ el, sizeU }) {
  const type = el.element_type || el.type;
  const meta = TYPE_META[type];
  const Icon = meta?.Icon;
  const hasPorts = (type === 'panel' || type === 'switch') && el.ports_summary;
  const compact = (sizeU || 1) < 2;

  if (compact) {
    // 1U (28px): for switch/panel show only port count; for others show only name.
    // No icon — 28px is too tight.
    return (
      <div className="rack-item-row">
        <span className="rack-item-title rack-item-title--1u">{el.name || el.code}</span>
        {hasPorts && <PortDot summary={el.ports_summary} />}
      </div>
    );
  }

  return (
    <>
      <div className="rack-item-header">
        {Icon && <Icon style={{ width: 20, height: 20, flexShrink: 0 }} />}
        <span className="rack-item-title">{el.name || el.code}</span>
      </div>
      <div className="rack-item-type">{meta?.label || type}</div>
      {hasPorts && <PortDot summary={el.ports_summary} />}
    </>
  );
}

function RackViewer({ companyId }) {
  const { t } = useTranslation();
  const { rackId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rack, setRack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hlId, setHlId] = useState(null);   // resaltado por ?destacar=<id>
  const [selectedElement, setSelectedElement] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // { u, side }
  const [portModalElement, setPortModalElement] = useState(null);
  const [componentsElement, setComponentsElement] = useState(null);
  const [elementConnections, setElementConnections] = useState([]);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [chassisItems, setChassisItems] = useState([]);
  const [powerSummary, setPowerSummary] = useState(null);
  const [powerOpen, setPowerOpen] = useState(true);
  const [fillerMode, setFillerMode] = useState(null); // 'tapa_ciega'|'pasa_hilos'|'bandeja'|null
  const [patchingElement, setPatchingElement] = useState(false);
  // Alta rápida de elementos con puertos SIN salir del rack: tipo elegido en la
  // barra («Añadir equipo») o null. El diálogo hereda rack y ubicación.
  const [quickCreateType, setQuickCreateType] = useState(null);
  // Hueco tocado en modo tablet: { u, side }. El alta rápida nace ya ahí.
  const [quickCreateAt, setQuickCreateAt] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [showPromote, setShowPromote] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // ── Modo tablet ─────────────────────────────────────────────────────────
  // En una tablet delante del armario no hay drag-and-drop (HTML5 DnD no
  // dispara con el dedo) ni sitio para tres columnas. El modo tablet cambia la
  // interacción: una cara cada vez, botones grandes y **tocar para colocar**.
  // Se activa solo con puntero grueso, pero se puede forzar y se recuerda.
  const coarsePointer = useTouchMode();
  const [touchPref, setTouchPref] = useState(() => {
    try { return localStorage.getItem('cmdb.rv.touch'); } catch { return null; }
  });
  const touchMode = touchPref == null ? coarsePointer : touchPref === 'on';
  const [faceTab, setFaceTab] = useState('front');
  const [placing, setPlacing] = useState(null);   // { el } → tocar una U libre
  const [sheetOpen, setSheetOpen] = useState(false);      // ficha del elemento
  const [unplacedOpen, setUnplacedOpen] = useState(false); // pendientes sin U
  const [addSheet, setAddSheet] = useState(null);          // { u, side } | null

  // Cada U mide más en la tablet: 36 px es un dedo de ancho, no un objetivo
  // cómodo. El resto de medidas (puertos, cables) se miden del DOM, así que
  // solo hay que cambiar aquí y en los estilos de la escala.
  const uPx = touchMode ? 46 : 36;

  const toggleTouchMode = () => {
    const next = !touchMode;
    setTouchPref(next ? 'on' : 'off');
    try { localStorage.setItem('cmdb.rv.touch', next ? 'on' : 'off'); } catch { /* sin persistencia */ }
    setPlacing(null);
    setSheetOpen(false);
    setUnplacedOpen(false);
    setAddSheet(null);
  };

  // ── Cableado en el frontal ───────────────────────────────────────────────
  // El visor ya colocaba y movía equipos; ahora además pinta sus puertos y los
  // cables que salen de ellos, y permite conectar con dos clics. Los datos son
  // los mismos del diseñador de cableado (`/api/cabling/graph`).
  const [cabling, setCabling] = useState({ nodes: [], edges: [] });
  const [cabSel, setCabSel] = useState(null); // { key, type, id, label, nodeTitle }
  const [cabPair, setCabPair] = useState(null); // { a, b }
  const [cabForm, setCabForm] = useState({ cable_type: 'Cat6', cable_color: 'azul' });
  const [cabSaving, setCabSaving] = useState(false);
  const [cabError, setCabError] = useState(null);
  const [canvasW, setCanvasW] = useState(0);
  const [portPos, setPortPos] = useState(() => new Map());
  const [cabLayout, setCabLayout] = useState({ frontRight: 0, channelLeft: 0, channelW: 60, canvasTop: 0, canvasH: 0 });
  const [scrollY, setScrollY] = useState(0);
  const frontCanvasRef = useRef(null);
  const ductCanvasRef = useRef(null);
  // Dos capas sobre el MISMO rack: «colocación» (como siempre, para montar y
  // mover) y «cableado» (puertos y cables; los nombres pasan al tooltip porque
  // encima de ellos no se lee nada). Se puede arrastrar en las dos.
  const [rackLayer, setRackLayer] = useState(() => {
    try { return localStorage.getItem('cmdb.rv.layer') || 'layout'; } catch { return 'layout'; }
  });
  const isCabLayer = rackLayer === 'cabling';
  const chooseLayer = (layer) => {
    setRackLayer(layer);
    setCabSel(null);
    setCabPair(null);
    try { localStorage.setItem('cmdb.rv.layer', layer); } catch { /* noop */ }
  };

  // ── Scroll propio de las U ──────────────────────────────────────────────
  // Un rack de 42U mide ~1.600 px: antes había que bajar TODA la página para
  // llegar a las U de abajo (y se iban las cabeceras, las barras y el panel
  // lateral). Ahora el scroll vive en `.rack-columns` (escala + slots) y, como
  // la frontal y la trasera representan LA MISMA U, se mueven sincronizadas:
  // si no, U42 de delante quedaría a la altura de U31 de detrás.
  const frontColsRef = useRef(null);
  const ductColsRef = useRef(null);
  const rearColsRef = useRef(null);
  const dualRef = useRef(null);
  const containerRef = useRef(null);

  // ── Pantalla completa (modo «trabajo») ──────────────────────────────────
  // En pantalla completa el rack se queda con todo el espacio: las dos barras
  // de acción (añadir equipo / añadir accesorio) y los KPIs se van a una barra
  // lateral izquierda, y el scroll de las U pasa a repartirse por flex (sin
  // medición), porque la cabecera y el menú de la app ya no están.
  // Usa la Fullscreen API; si el navegador la bloquea (iframe, iOS…) cae a un
  // modo CSS `position: fixed` y se sale con Esc.
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(containerRef.current) && document.fullscreenElement === containerRef.current);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    // Solo en el modo de respaldo: en pantalla completa «de verdad» la salida
    // con Esc la gestiona el navegador.
    if (!isFullscreen || document.fullscreenElement) return undefined;
    const onKeyDown = (event) => { if (event.key === 'Escape') setIsFullscreen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isFullscreen]);

  const toggleFullscreen = async () => {
    const el = containerRef.current;
    if (document.fullscreenElement) {
      try { await document.exitFullscreen(); } catch { /* el navegador ya salió */ }
      setIsFullscreen(false);
      return;
    }
    if (isFullscreen) { setIsFullscreen(false); return; }
    try {
      if (el?.requestFullscreen) await el.requestFullscreen();
      // Se fija el estado aquí y no solo en `fullscreenchange`: el evento llega
      // tarde (o no llega en pestañas en segundo plano) y la UI se quedaba a
      // medias. El listener sigue haciendo falta para la salida con Esc.
      setIsFullscreen(true);
    } catch {
      // Sin permiso para la Fullscreen API: modo CSS a pantalla completa.
      setIsFullscreen(true);
    }
  };

  /* El alto del área de U lo pone ahora el flex del layout (ver `RackViewer.css`):
     el lienzo ocupa el hueco que queda bajo la cabecera y las barras, y cada
     columna hace su propio scroll. Antes se medía en píxeles desde aquí con
     `getBoundingClientRect`, y esa cifra se quedaba corta en cuanto cambiaba el
     ancho de la ventana (las barras pasaban de una a dos filas), dejando el rack
     recortado y el conjunto como una isla. */

  const handleColumnsScroll = (event) => {
    const source = event.currentTarget;
    setScrollY(source.scrollTop);
    // Frontal, columna de cableado y trasero representan LA MISMA U: si no van
    // a la misma altura, el cable de U40 saldría a la altura de U20.
    [frontColsRef.current, ductColsRef.current, rearColsRef.current].forEach((target) => {
      if (!target || target === source) return;
      if (Math.abs(target.scrollTop - source.scrollTop) < 1) return;
      target.scrollTop = source.scrollTop;
    });
  };

  /** Quick-patch an element field (full_depth, pdu_phase, pdu_amperage, parent_pdu_id) and reload. */
  const handleElementPatch = async (elementId, patch) => {
    try {
      setPatchingElement(true);
      await api.updateElement(elementId, patch, companyId);
      await fetchRack();
    } catch (e) {
      setError(e.message || 'Error al actualizar el elemento');
    } finally {
      setPatchingElement(false);
    }
  };

  /** Marca el equipo como validado en el inventario (checklist de montaje). */
  const verifyElement = async (el) => {
    if (!el?.id) return;
    setVerifying(true);
    try {
      await api.updateElement(el.id, { install_state: 'verified' }, companyId);
      setSheetOpen(false);
      await fetchRack();
    } catch (e) {
      setError(e.message || 'No se pudo validar el elemento');
    } finally {
      setVerifying(false);
    }
  };

  useEffect(() => {
    if (!companyId) {
      setError(t('msg.select_company') || 'Selecciona una empresa para ver el rack.');
      setLoading(false);
      return;
    }
    fetchRack();
  }, [rackId, companyId]);

  useEffect(() => {
    setSelectedElement(null);
  }, [rack]);

  // La hoja de detallas solo tiene sentido con un equipo seleccionado.
  useEffect(() => {
    if (!selectedElement) setSheetOpen(false);
  }, [selectedElement]);

  /**
   * ?destacar=<id> — llega desde la ficha de un servidor («Ver rack»).
   * Resalta y centra ese equipo dentro del rack y limpia el parámetro.
   */
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !rack) return undefined;
    const id = Number(raw);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      return next;
    }, { replace: true });
    const placed = (rack.elements || []).some((el) => el.id === id);
    if (!placed) return undefined;
    setHlId(id);
    const node = document.getElementById(`rack-el-${id}`);
    if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, rack, setSearchParams]);

  useEffect(() => {
    if (!selectedElement?.id || !companyId) {
      setElementConnections([]);
      return;
    }
    const loadConnections = async () => {
      try {
        setLoadingConnections(true);
        const resp = await api.getElementConnections(selectedElement.id, companyId);
        setElementConnections(resp.data || []);
      } catch (err) {
        console.warn('No se pudieron cargar las conexiones:', err);
        setElementConnections([]);
      } finally {
        setLoadingConnections(false);
      }
    };
    loadConnections();
  }, [selectedElement?.id, companyId]);

  const fetchRack = async () => {
    try {
      setLoading(true);
      setError(null);

      const [rackResp, chassisResp] = await Promise.all([
        api.getRack(rackId, companyId),
        api.getChassis({ company_id: companyId }),
      ]);

      setRack(rackResp.data || rackResp);
      setChassisItems(Array.isArray(chassisResp?.data) ? chassisResp.data : []);
      // Checklist de montaje (best-effort: no debe tumbar el visor).
      try {
        const rdResp = await api.getRackReadiness(rackId, companyId);
        setReadiness(rdResp.data || null);
      } catch { setReadiness(null); }
      // Power summary (best-effort; may fail if PDU feature not migrated yet)
      try {
        const pwResp = await api.getRackPowerSummary(rackId, companyId);
        setPowerSummary(pwResp || null);
      } catch { setPowerSummary(null); }
    } catch (err) {
      setError(err.message);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getElementColor = (type) => {
    const colors = {
      server: '#007bff',
      pdu: '#ffc107',
      ups: '#f59e0b',
      router: '#7c3aed',
      firewall: '#dc2626',
      panel: '#17a2b8',
      switch: '#28a745',
      chassis: '#8b5cf6',
      tray: '#6c757d',
      blank: '#e9ecef'
    };
    return colors[type] || '#6c757d';
  };

  const isNodeServer = (el) =>
    (el?.element_type || el?.type) === 'server' &&
    (((el?.configuration || {}).form_factor === 'node') || el?.form_factor === 'node');

  const normalizeChassis = (ch) => ({
    ...ch,
    type: 'chassis',
    element_type: 'chassis',
    position_front: true,
    size_u: ch.size_u || 2,
    source_kind: 'chassis',
    source_id: ch.id,
    code: ch.name,
  });

  const rackIdNum = Number(rackId);
  const rackLocationId = rack?.location_id || rack?.location?.id || null;

  const rackElements = useMemo(
    () => (rack?.elements || []).filter((e) => !isNodeServer(e)).map((e) => ({
      ...e,
      source_kind: 'element',
      source_id: e.id,
    })),
    [rack]
  );

  const rackChassis = useMemo(() => {
    return (chassisItems || [])
      .filter((c) => {
        if (c.rack_id === rackIdNum) return true;
        // Also show unassigned chassis from same location as installable in this rack
        return !c.rack_id && rackLocationId && c.location_id === rackLocationId;
      })
      .map(normalizeChassis);
  }, [chassisItems, rackIdNum, rackLocationId]);

  const elements = useMemo(() => [...rackElements, ...rackChassis], [rackElements, rackChassis]);
  // position_front defaults to true (front) when not explicitly false
  const placedFront = useMemo(() => elements.filter((e) => (e.position_u || e.position) && e.position_front !== false), [elements]);
  const placedRear  = useMemo(() => elements.filter((e) => (e.position_u || e.position) && e.position_front === false),  [elements]);
  const placed      = useMemo(() => [...placedFront, ...placedRear], [placedFront, placedRear]);
  const unplaced    = useMemo(() => elements.filter((e) => !(e.position_u || e.position)), [elements]);

  const fillers      = useMemo(() => rack?.fillers || [], [rack]);
  const fillersFront = useMemo(() => fillers.filter(f => f.side !== 'rear'), [fillers]);
  const fillersRear  = useMemo(() => fillers.filter(f => f.side === 'rear'), [fillers]);
  const heightU = rack?.height_u || 42;
  const isDraft = rack?.status === 'draft';
  const pendingValidation = readiness?.summary?.pending_validation ?? 0;
  const readinessById = useMemo(() => {
    const map = new Map();
    (readiness?.elements || []).forEach((item) => map.set(item.id, item));
    return map;
  }, [readiness]);
  const slotNumbers = useMemo(() => Array.from({ length: heightU }, (_, idx) => heightU - idx), [heightU]);

  // U positions in the rear panel that are blocked by a full-depth front element
  const fullDepthFrontUs = useMemo(() => {
    const blocked = new Set();
    placedFront.forEach(el => {
      if (el.full_depth) {
        const startU = el.position_u || el.position;
        const sizeU  = el.size_u || el.size || 1;
        if (startU) {
          for (let u = startU; u <= startU + sizeU - 1; u++) blocked.add(u);
        }
      }
    });
    return blocked;
  }, [placedFront]);

  // U positions in the front panel that are blocked by a full-depth rear element
  const fullDepthRearUs = useMemo(() => {
    const blocked = new Set();
    placedRear.forEach(el => {
      if (el.full_depth) {
        const startU = el.position_u || el.position;
        const sizeU  = el.size_u || el.size || 1;
        if (startU) {
          for (let u = startU; u <= startU + sizeU - 1; u++) blocked.add(u);
        }
      }
    });
    return blocked;
  }, [placedRear]);

  const frontOccupiedUs = useMemo(() => {
    const occupied = new Set(fullDepthRearUs);
    placedFront.forEach((el) => {
      const startU = el.position_u || el.position;
      const sizeU = el.size_u || el.size || 1;
      for (let u = startU; u <= startU + sizeU - 1; u++) occupied.add(u);
    });
    fillersFront.forEach((filler) => occupied.add(filler.position_u));
    return occupied;
  }, [placedFront, fillersFront, fullDepthRearUs]);

  const rearOccupiedUs = useMemo(() => {
    const occupied = new Set(fullDepthFrontUs);
    placedRear.forEach((el) => {
      const startU = el.position_u || el.position;
      const sizeU = el.size_u || el.size || 1;
      for (let u = startU; u <= startU + sizeU - 1; u++) occupied.add(u);
    });
    fillersRear.forEach((filler) => occupied.add(filler.position_u));
    return occupied;
  }, [placedRear, fillersRear, fullDepthFrontUs]);

  const frontUsedU = frontOccupiedUs.size;
  const rearUsedU = rearOccupiedUs.size;
  const frontFreeU = heightU - frontUsedU;
  const rearFreeU = heightU - rearUsedU;
  const fullDepthCount = placedFront.filter((el) => el.full_depth).length;
  const powerPdus = powerSummary?.pdus || [];
  const powerDevices = powerSummary?.devices || [];
  const powerOutletsTotal = powerPdus.reduce((sum, pdu) => sum + (pdu.outlets_total || 0), 0);
  const powerOutletsUsed = powerPdus.reduce((sum, pdu) => sum + (pdu.outlets_used || 0), 0);
  const redundantPowerDevices = powerDevices.filter((device) => device.is_redundant).length;

  const getPowerStateMeta = (device) => {
    if (device.is_redundant) {
      return { label: 'Redundante', tone: 'ok' };
    }
    if (device.pdu_count === 1) {
      return { label: 'Fuente única', tone: 'warn' };
    }
    return { label: 'Sin alimentación', tone: 'off' };
  };

  const handleSlotClick = async (u, side) => {
    // 1) Modo colocar: el siguiente toque en un hueco libre mueve el equipo.
    if (placing) {
      await moveElementTo(placing.el, u, side);
      return;
    }
    // 2) Modo accesorio: colocar/quitar tapas y bandejas (igual que siempre).
    if (fillerMode) {
      const occupied = side === 'front' ? frontOccupiedUs.has(u) : rearOccupiedUs.has(u);
      const hasFiller = (side === 'front' ? fillersFront : fillersRear)
        .some(f => f.position_u === u);
      const blockedByFullDepth = side === 'rear'
        ? fullDepthFrontUs.has(u)
        : fullDepthRearUs.has(u);
      if (occupied || hasFiller || blockedByFullDepth) return;
      try {
        const resp = await api.addRackFiller(rackId, companyId, u, side, fillerMode);
        setRack(prev => ({ ...prev, fillers: resp.data }));
      } catch (e) { console.error('filler add:', e); }
      return;
    }
    // 3) Modo tablet: tocar un hueco libre del rack abre el alta rápida ahí.
    if (touchMode) {
      const occupied = side === 'front' ? frontOccupiedUs.has(u) : rearOccupiedUs.has(u);
      if (occupied) return;
      setAddSheet({ u, side });
    }
  };

  const handleFillerClick = async (filler) => {
    try {
      const resp = await api.removeRackFiller(rackId, companyId, filler.position_u, filler.side);
      setRack(prev => ({ ...prev, fillers: resp.data }));
    } catch (e) { console.error('filler remove:', e); }
  };

  /**
   * Elemento creado desde el propio visor: recarga el rack y lo resalta unos
   * segundos para que se vea dónde ha quedado.
   */
  const handleQuickCreated = async (newId) => {
    setQuickCreateType(null);
    setQuickCreateAt(null);
    await fetchRack();
    if (newId) {
      setHlId(newId);
      setTimeout(() => setHlId(null), 4000);
      setTimeout(() => {
        document.getElementById(`rack-el-${newId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 250);
    }
  };

  // ── Geometría de colocación ──────────────────────────────────────────────
  // Las cuentas viven en `utils/rackPlacement.js` para que el arrastre (ratón)
  // y el toque (tablet) no puedan discrepar.
  const getRange = (startU, sizeU) => rangeOf(startU, sizeU);
  const rangesOverlap = (a, b) => rangesOverlapPure(a, b);

  /** ¿Cabe `el` (o un equipo del tamaño indicado) con su U superior en `topU`? */
  const canPlaceAt = (topU, side, el) => {
    const size = el?.size_u || el?.size || 1;
    // El propio equipo no se estorba a sí mismo si se mueve dentro de la misma cara.
    const sameSide = el ? (side === 'front') === (el.position_front !== false) : false;
    return canPlaceAtPure({
      topU,
      sizeU: size,
      heightU,
      occupied: side === 'front' ? frontOccupiedUs : rearOccupiedUs,
      blocked: side === 'front' ? fullDepthRearUs : fullDepthFrontUs,
      ignore: sameSide ? elementUs(el) : [],
    });
  };

  const selectElement = (el) => {
    setSelectedElement(el);
    if (touchMode) setSheetOpen(true);
  };

  /** Entra en «modo colocar»: el siguiente toque en una U libre mueve el equipo. */
  const beginMove = (el) => {
    if (!el) return;
    setPlacing({ el });
    setFaceTab(el.position_front === false ? 'rear' : 'front');
    setSelectedElement(el);
    setSheetOpen(false);
    setUnplacedOpen(false);
    setAddSheet(null);
    setFillerMode(null);
    setError(null);
  };

  const cancelPlacing = () => setPlacing(null);

  /**
   * Mueve (o monta) un equipo a la U SUPERIOR tocada. Es el mismo camino que el
   * «soltar» del ratón, pero sin `dataTransfer`: en una tablet no existe.
   */
  const moveElementTo = async (el, topU, side) => {
    if (!el) return;
    const size = el.size_u || el.size || 1;
    if (!canPlaceAt(topU, side, el)) {
      setError(`No cabe en U${topU}: el hueco está ocupado o bloqueado por profundidad completa.`);
      return;
    }
    const startU = topU - size + 1;
    const isFront = side === 'front';
    try {
      setSaving(true);
      setError(null);
      if ((el.source_kind || 'element') === 'chassis') {
        await api.updateChassis(el.id, {
          rack_id: rackIdNum,
          location_id: rackLocationId || undefined,
          position_u: startU,
        }, companyId);
      } else {
        await api.updateRackElementPosition(rackId, el.id, {
          position_u: startU,
          position_front: isFront,
        }, companyId);
      }
      setPlacing(null);
      setSelectedElement(null);
      setFaceTab(side);
      await fetchRack();
      setHlId(el.id);
      setTimeout(() => setHlId(null), 3000);
    } catch (err) {
      setError(err.message || 'No se pudo mover el elemento');
    } finally {
      setSaving(false);
    }
  };

  /** Saca el equipo del rack (vuelve a «pendientes sin U»). */
  const unplaceElement = async (el) => {
    if (!el) return;
    try {
      setSaving(true);
      setError(null);
      if ((el.source_kind || 'element') === 'chassis') {
        await api.updateChassis(el.id, {
          position_u: null,
          rack_id: rackIdNum,
          location_id: rackLocationId || undefined,
        }, companyId);
      } else {
        await api.updateRackElementPosition(rackId, el.id, { position_u: null }, companyId);
      }
      setPlacing(null);
      setSelectedElement(null);
      setSheetOpen(false);
      await fetchRack();
    } catch (err) {
      setError(err.message || 'No se pudo descolocar el elemento');
    } finally {
      setSaving(false);
    }
  };

  const formatRange = (startU, sizeU) => {
    if (!startU) return 'Pendiente';
    const len = sizeU || 1;
    const top = startU + len - 1;
    return len === 1 ? `U${startU}` : `U${top} – U${startU}`;
  };

  const getElementTopU = (el) => {
    const startU = el.position_u || el.position;
    const sizeU = el.size_u || el.size || 1;
    return startU ? startU + sizeU - 1 : null;
  };

  const getOccupancyPct = (used) => (heightU > 0 ? Math.min(100, Math.round((used / heightU) * 100)) : 0);

  /** Puertos clicables dentro de una banda del frontal. */
  const renderPorts = (el) => {
    const node = nodeOf(el);
    const geo = portGridOf(node, el.size_u || el.size || 1);
    if (!geo) return null;
    return (
      <div className="rv-ports">
        {(node.ports || []).map((port, index) => {
          const col = index % geo.cols;
          const row = Math.floor(index / geo.cols);
          const busy = (port.edges || []).length > 0 || port.external;
          const isSel = cabSel?.key === port.key;
          const rightOfCol = geo.rightPad + (geo.cols - 1 - col) * (geo.portW + geo.gap);
          const showNum = geo.portW >= 11;
          return (
            <button
              key={port.key}
              type="button"
              className={`rv-port${busy ? ' is-busy' : ''}${isSel ? ' is-selected' : ''}${hot.ports.has(port.key) ? ' is-hot' : ''}`}
              style={{
                right: `${rightOfCol}px`,
                top: `${row * geo.rowH + (geo.rowH - geo.portH) / 2}px`,
                width: `${geo.portW}px`,
                height: `${geo.portH}px`,
              }}
              draggable={false}
              data-port-key={port.key}
              onDragStart={(event) => { event.preventDefault(); event.stopPropagation(); }}
              onMouseDown={(event) => event.stopPropagation()}
              onMouseEnter={(event) => {
                setHoverPortKey(port.key);
                const board = frontCanvasRef.current?.closest('.rack-board');
                const bb = board?.getBoundingClientRect();
                const r = event.currentTarget.getBoundingClientRect();
                // A la IZQUIERDA del puerto: a la derecha están el canal y los cables.
                if (bb) setHoverTip({ x: r.left - bb.left - 10, y: r.top - bb.top - 4 });
              }}
              onMouseLeave={() => { setHoverPortKey(null); setHoverTip(null); }}
              onClick={(event) => handlePortClick(event, el, node, port, geo, index)}
              title={`${node.title} · ${port.label}${busy ? ' · con cable' : ' · libre (clic para conectar)'}`}
            >
              <span className="rv-port-jack" aria-hidden="true" />
              {showNum && <span className="rv-port-num">{port.number ?? index + 1}</span>}
            </button>
          );
        })}
      </div>
    );
  };

  // ── Cableado: carga, puertos y cables ────────────────────────────────────
  const loadCabling = useCallback(async () => {
    // La empresa se toma del RACK: al recargar en frío la selección puede ser
    // otra y el grafo saldría vacío (sin puertos ni cables).
    const cabCompanyId = rack?.company_id || companyId;
    if (!cabCompanyId || !rackId) return;
    try {
      const res = await api.getCablingGraph(
        { scope_key: `racks:${rackId}`, rack_ids: String(rackId), include_unconnected: true },
        cabCompanyId,
      );
      setCabling(res?.data || { nodes: [], edges: [] });
    } catch {
      setCabling({ nodes: [], edges: [] });
    }
  }, [companyId, rackId, rack]);

  useEffect(() => { loadCabling(); }, [loadCabling]);

  // Ancho del lienzo: lo necesitan los cables para saber dónde está el canal.
  useEffect(() => {
    const el = frontCanvasRef.current;
    if (!el) return undefined;
    const measure = () => setCanvasW(el.clientWidth || 0);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [heightU, loading]);

  // Nodo del grafo de cada equipo del rack (switch, panel, servidor…).
  const cablingByElement = useMemo(() => {
    const map = new Map();
    (cabling.nodes || []).forEach((node) => {
      if (!node.rack_id) return;
      map.set(`${node.kind}:${node.id}`, node);
    });
    return map;
  }, [cabling]);

  const nodeOf = (el) => cablingByElement.get(`${el.element_type || el.type}:${el.id}`) || null;

  /** puerto (clave) → { node, port, index } de todo el grafo del rack. */
  const portIndex = useMemo(() => {
    const map = new Map();
    (cabling.nodes || []).forEach((node) => {
      (node.ports || []).forEach((port, index) => map.set(port.key, { node, port, index }));
    });
    return map;
  }, [cabling]);

  // Al pasar por un puerto se iluminan SUS cables y los puertos del otro extremo.
  const [hoverPortKey, setHoverPortKey] = useState(null);
  const [hoverTip, setHoverTip] = useState(null);
  const hot = useMemo(() => {
    const empty = { ports: new Set(), edges: new Set(), links: [], hasPath: false };
    if (!hoverPortKey) return empty;
    // Camino COMPLETO desde el puerto bajo el ratón: se salta de puerto a
    // puerto por los cables, así que da igual en qué punto del camino estemos
    // (switch, boca del panel, toma…): se enciende todo lo que sigue.
    const path = tracePath(
      cabling.edges || [],
      hoverPortKey,
      (edge, i) => `${edge.kind}-${edge.linked_id || i}`,
    );
    const links = [];
    (cabling.edges || []).forEach((edge) => {
      if (edge.a !== hoverPortKey && edge.b !== hoverPortKey) return;
      const other = portIndex.get(edge.a === hoverPortKey ? edge.b : edge.a);
      links.push({
        title: other ? `${other.node.title} · ${other.port.label}` : 'fuera del rack',
        // La toma de pared no está en el armario: vive en su sede.
        location: other?.node?.kind === 'outlet' ? other.node.location_name || '' : '',
        cable: edge.cable_type || '',
        color: edge.cable_color || '',
      });
    });
    return { ports: path.ports, edges: path.edges, links, hasPath: path.edges.size > 0 };
  }, [hoverPortKey, cabling, portIndex]);

  /** Rejilla de puertos: muchos llenan todo el ancho; pocos, compactos a la derecha. */
  const portGridOf = (node, sizeU) => {
    const ports = node?.ports || [];
    if (!ports.length) return null;
    const itemH = sizeU * uPx + (sizeU - 1) * 2;
    const rows = ports.length > 24 ? 2 : 1;
    const cols = Math.ceil(ports.length / rows);
    const gap = 2;
    // Hueco a la derecha para que el cable salga limpio al canal.
    const rightPad = 16;
    const avail = Math.max(60, (canvasW || 220) - rightPad - 8);
    // El puerto llena el ancho disponible; solo se limita (tope alto) para que
    // un equipo de 2-4 puertos no quede con conectores gigantes.
    const portW = Math.max(5, Math.min((avail - (cols - 1) * gap) / cols, 26));
    const rowH = itemH / rows;
    const portH = Math.max(8, Math.min(rowH - 6, 18));
    return { cols, rows, itemH, rowH, portW, portH, gap, rightPad };
  };

  // Mide, en coordenadas del TABLERO (no de la columna que hace scroll): el
  // centro de cada puerto (x fija + y de contenido, independiente del scroll) y
  // los bordes de las columnas. Con esto una sola capa dibuja todos los cables y
  // acompaña al scroll con un simple translate.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const dual = dualRef.current;
    const canvas = frontCanvasRef.current;
    const duct = ductCanvasRef.current;
    if (!dual || !canvas) return;
    const dualBox = dual.getBoundingClientRect();
    const canvasBox = canvas.getBoundingClientRect();
    const scroll = frontColsRef.current?.scrollTop || 0;
    const next = new Map();
    canvas.querySelectorAll('[data-port-key]').forEach((el) => {
      const r = el.getBoundingClientRect();
      next.set(el.getAttribute('data-port-key'), {
        x: r.left - dualBox.left + r.width / 2,     // x en el tablero
        y: r.top - canvasBox.top + scroll + r.height / 2, // y de contenido
        h: r.height,
      });
    });
    const lay = {
      frontRight: canvasBox.right - dualBox.left,
      channelLeft: duct ? duct.getBoundingClientRect().left - dualBox.left : canvasBox.right - dualBox.left + 6,
      channelW: duct ? duct.getBoundingClientRect().width : 60,
      canvasTop: canvasBox.top - dualBox.top,
      canvasH: canvasBox.height,
    };
    if (!next.size) return;
    setCabLayout((prev) => (
      Math.abs(prev.frontRight - lay.frontRight) < 1
      && Math.abs(prev.channelLeft - lay.channelLeft) < 1
      && Math.abs(prev.canvasTop - lay.canvasTop) < 1
        ? prev : lay
    ));
    setPortPos((prev) => {
      if (prev.size === next.size) {
        let same = true;
        next.forEach((value, key) => {
          const old = prev.get(key);
          if (!old || Math.abs(old.x - value.x) > 1 || Math.abs(old.y - value.y) > 1) same = false;
        });
        if (same) return prev;
      }
      return next;
    });
  });

  const handlePortClick = (e, el, node, port, geo, index) => {
    e.stopPropagation();
    if (cabSel && cabSel.key === port.key) { setCabSel(null); setCabPair(null); return; }
    const port_ = { key: port.key, type: port.type, id: port.id, label: port.label || `P${index + 1}`, nodeTitle: node.title };
    const busy = (port.edges || []).length > 0 || port.external;
    if (!cabSel) {
      if (busy) return; // un puerto ocupado se gestiona desde «Puertos» (doble clic)
      setCabSel(port_);
      return;
    }
    if (busy) { setCabError('El segundo puerto ya tiene un cable'); return; }
    setCabPair({ a: cabSel, b: port_ });
    setCabSel(null);
    const fiber = String(port.type).includes('panel') && String(cabSel.type).includes('panel');
    setCabForm({ cable_type: fiber ? 'Fibra SM (OS2)' : 'Cat6', cable_color: fiber ? 'violeta' : 'azul' });
    setCabError(null);
  };

  const doConnect = async () => {
    if (!cabPair || cabSaving) return;
    setCabSaving(true);
    setCabError(null);
    try {
      await api.createCablingLink(
        { type: cabPair.a.type, id: cabPair.a.id },
        { type: cabPair.b.type, id: cabPair.b.id },
        { cable_type: cabForm.cable_type || null, cable_color: cabForm.cable_color || null },
        rack?.company_id || companyId,
      );
      setCabPair(null);
      await Promise.all([loadCabling(), fetchRack()]);
    } catch (e) {
      setCabError(e.message);
    } finally {
      setCabSaving(false);
    }
  };

  /** Cables en UNA sola capa (coordenadas del tablero): del puerto, por el canal
   *  central y de vuelta. Se reparten en carriles para no solaparse. */
  const cableGeoms = useMemo(() => {
    if (!canvasW || !cabLayout.channelLeft) return [];
    const elByNode = new Map();
    elements.forEach((el) => elByNode.set(`${el.element_type || el.type}:${el.id}`, el));

    // 1) Resolver extremos a coordenadas del tablero (medidas del DOM).
    const raw = [];
    (cabling.edges || []).forEach((edge, i) => {
      const a = portIndex.get(edge.a);
      const b = portIndex.get(edge.b);
      if (!a || !b) return;
      const elA = elByNode.get(`${a.node.kind}:${a.node.id}`);
      const elB = elByNode.get(`${b.node.kind}:${b.node.id}`);
      if (!elA || !elB) return;
      if (elA.position_front === false || elB.position_front === false) return;
      const pa = portPos.get(edge.a);
      const pb = portPos.get(edge.b);
      if (!pa || !pb || !Number.isFinite(pa.y) || !Number.isFinite(pb.y)) return;
      raw.push({
        key: `${edge.kind}-${edge.linked_id || i}`,
        y1: Math.min(pa.y, pb.y), y2: Math.max(pa.y, pb.y),
        pa, pb,
        color: edge.kind === 'backbone' ? '#8b5cf6' : colorHex(edge.cable_color || 'azul'),
        dashed: edge.kind === 'backbone' || String(edge.cable_type || '').includes('Fibra'),
        kind: edge.kind, a: edge.a, b: edge.b,
      });
    });

    // 2) Carril en el canal: cables que se solapan en vertical no comparten carril.
    const sorted = [...raw].sort((a, b) => a.y1 - b.y1 || a.y2 - b.y2);
    const laneEnd = [];
    const laneOf = new Map();
    sorted.forEach((c) => {
      let lane = laneEnd.findIndex((end) => end <= c.y1 - 4);
      if (lane === -1) { lane = laneEnd.length; laneEnd.push(c.y2); }
      else laneEnd[lane] = c.y2;
      laneOf.set(c.key, lane);
    });
    const lanes = Math.max(1, laneEnd.length);
    const step = Math.max(5, Math.min(13, (cabLayout.channelW - 12) / lanes));
    // El cable baja un poco (por DEBAJO del conector) y recorre en horizontal por
    // ese hueco hasta el canal: así nunca cruza por encima de los puertos.
    const exitX = cabLayout.frontRight + 3;
    return raw.map((c) => {
      const lane = laneOf.get(c.key) || 0;
      const laneX = cabLayout.channelLeft + 6 + lane * step;
      const dropA = (c.pa.h || 14) / 2 + 3;
      const dropB = (c.pb.h || 14) / 2 + 3;
      const ya = c.pa.y + dropA;   // justo bajo el conector A
      const yb = c.pb.y + dropB;   // justo bajo el conector B
      return {
        key: c.key,
        d: `M${c.pa.x},${c.pa.y} V${ya} H${exitX} H${laneX} V${yb} H${exitX} H${c.pb.x} V${c.pb.y}`,
        color: c.color,
        dashed: c.dashed,
        kind: c.kind,
        a: c.a, b: c.b,
      };
    });
  }, [cabling, canvasW, cabLayout, elements, portPos]); // eslint-disable-line react-hooks/exhaustive-deps


  const renderOccupancyBar = (label, used, free, tone) => {
    const pct = getOccupancyPct(used);
    return (
      <div className="rv-capacity-row">
        <div className="rv-capacity-row-head">
          <span>{label}</span>
          <span>{used}/{heightU}U ocupadas · {free}U libres</span>
        </div>
        <div className="rv-capacity-track">
          <div className={`rv-capacity-fill rv-capacity-fill--${tone}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  const handleDragStart = (ev, element) => {
    ev.dataTransfer.setData('application/json', JSON.stringify({
      id: element.id,
      source_kind: element.source_kind || 'element',
      size: element.size_u || element.size || 1,
      position_front: element.position_front !== false,
    }));
    ev.dataTransfer.effectAllowed = 'move';
    setDraggingId(`${element.source_kind || 'element'}:${element.id}`);
    setError(null);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDropTarget(null);
  };

  const handleDropOnUnplaced = async (ev) => {
    ev.preventDefault();
    setDropTarget(null);
    const data = ev.dataTransfer.getData('application/json');
    if (!data) return;
    const parsed = JSON.parse(data);
    const el = placed.find((e) => e.id === parsed.id && (e.source_kind || 'element') === (parsed.source_kind || 'element'));
    if (!el) return; // already unplaced
    try {
      setSaving(true);
      if ((parsed.source_kind || 'element') === 'chassis') {
        await api.updateChassis(parsed.id, { position_u: null, rack_id: rackIdNum, location_id: rackLocationId || undefined }, companyId);
      } else {
        await api.updateRackElementPosition(rackId, parsed.id, { position_u: null }, companyId);
      }
      await fetchRack();
    } catch (err) {
      setError(err.message || 'No se pudo descolocar el elemento');
    } finally {
      setSaving(false);
    }
  };

  const handleDropOnU = async (ev, targetU, side) => {
    ev.preventDefault();
    setDropTarget(null);
    const data = ev.dataTransfer.getData('application/json');
    if (!data) return;
    const parsed = JSON.parse(data);
    const size = parsed.size || 1;
    const isFront = side === 'front';
    const startU = targetU - size + 1;
    const dropRange = getRange(startU, size);
    if (!dropRange || dropRange.bottom < 1 || dropRange.top > heightU) return;
    // check collisions only within same side
    const sidePool = isFront ? placedFront : placedRear;
    const collides = sidePool.some((e) => {
      if (e.id === parsed.id) return false;
      const existingRange = getRange(e.position_u || e.position, e.size_u || e.size || 1);
      return rangesOverlap(existingRange, dropRange);
    });
    if (collides) {
      setError('La posición se solapa con otro elemento.');
      return;
    }
    // Check if drop is blocked by a full-depth element on the opposite face
    const oppositeFullDepth = isFront ? fullDepthRearUs : fullDepthFrontUs;
    const oppositeBlocked = Array.from({ length: size }, (_, i) => targetU - i)
      .some(u => oppositeFullDepth.has(u));
    if (oppositeBlocked) {
      setError(isFront
        ? 'Posición bloqueada: hay un elemento de profundidad completa en la trasera.'
        : 'Posición bloqueada: hay un elemento de profundidad completa en el frontal.');
      return;
    }
    try {
      setSaving(true);
      if ((parsed.source_kind || 'element') === 'chassis') {
        // Chassis are always frontal in current model
        await api.updateChassis(parsed.id, {
          rack_id: rackIdNum,
          location_id: rackLocationId || undefined,
          position_u: startU,
        }, companyId);
      } else {
        await api.updateRackElementPosition(rackId, parsed.id, { position_u: startU, position_front: isFront }, companyId);
      }
      await fetchRack();
    } catch (err) {
      setError(err.message || 'No se pudo actualizar la posición');
    } finally {
      setSaving(false);
    }
  };

  const allowDrop = (ev, u, side) => {
    ev.preventDefault();
    if (u !== undefined) setDropTarget({ u, side });
  };

  if (loading) {
    return <div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div>;
  }

  /* ── Piezas de UI que cambian de sitio en pantalla completa ──────────────
     En vista normal: KPIs en la cabecera y las dos barras encima del rack.
     En pantalla completa: todo a la barra lateral izquierda, para que el rack
     se lleve el alto y el ancho. */

  const statChips = (
    <>
      <div className="rv-stat-chip">
        <span className="rv-stat-n">{heightU}U</span>
        <span className="rv-stat-lbl">total</span>
      </div>
      <div className="rv-stat-chip rv-stat-chip--used">
        <span className="rv-stat-n">F {frontUsedU}U</span>
        <span className="rv-stat-lbl">frontal</span>
      </div>
      <div className="rv-stat-chip rv-stat-chip--free">
        <span className="rv-stat-n">R {rearUsedU}U</span>
        <span className="rv-stat-lbl">trasera</span>
      </div>
      {fullDepthCount > 0 && (
        <div className="rv-stat-chip rv-stat-chip--depth">
          <span className="rv-stat-n">{fullDepthCount}</span>
          <span className="rv-stat-lbl">full depth</span>
        </div>
      )}
      {unplaced.length > 0 && (
        <div className="rv-stat-chip rv-stat-chip--pending">
          <span className="rv-stat-n">{unplaced.length}</span>
          <span className="rv-stat-lbl">sin ubicar</span>
        </div>
      )}
      {pendingValidation > 0 && (
        <div
          className="rv-stat-chip rv-stat-chip--pending"
          title="Equipos que aún no están validados en el CMDB"
        >
          <span className="rv-stat-n">{pendingValidation}</span>
          <span className="rv-stat-lbl">sin validar</span>
        </div>
      )}
    </>
  );

  const addElementBar = (
    <div className="rack-filler-toolbar rack-add-toolbar">
      <span className="rack-filler-toolbar-label">
        {t('rack.add_element')}:
      </span>
      {[
        ...(isDraft
          ? [{ type: 'server', label: 'Servidor', icon: '🖥️', color: '#2563eb' }]
          : []),
        { type: 'switch', label: t('rack.add_type_switch'), icon: '🔀', color: '#28a745' },
        { type: 'panel',  label: t('rack.add_type_panel'),  icon: '🔗', color: '#17a2b8' },
        { type: 'pdu',    label: t('rack.add_type_pdu'),    icon: '⚡', color: '#ffc107' },
      ].map((item) => (
        <button
          key={item.type}
          className="rack-filler-btn rack-add-btn"
          style={{ borderColor: item.color, color: item.color }}
          onClick={() => setQuickCreateType(item.type)}
          title={`${t('rack.add_element')} · ${item.label}`}
        >
          {item.icon} {item.label}
        </button>
      ))}
      <span className="rack-filler-hint">Rack y ubicación se toman de esta vista · el nombre arranca en el siguiente de la nomenclatura</span>
    </div>
  );

  const fillerBar = (
    <div className="rack-filler-toolbar">
      <span className="rack-filler-toolbar-label">Añadir accesorio:</span>
      {Object.entries(FILLER_META).map(([type, meta]) => (
        <button
          key={type}
          className={`rack-filler-btn${fillerMode === type ? ' rack-filler-btn--active' : ''}`}
          style={fillerMode === type ? { background: meta.color, borderColor: meta.color } : {}}
          onClick={() => setFillerMode(fillerMode === type ? null : type)}
        >
          {meta.icon} {meta.label}
        </button>
      ))}
      {fillerMode && (
        <span className="rack-filler-hint">
          Toca (o haz clic en) un hueco libre para colocar · en un relleno para quitar
        </span>
      )}
    </div>
  );

  const unplacedPanel = (
    <aside
      className="rack-unplaced"
      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('rack-unplaced--over'); }}
      onDragLeave={(e) => e.currentTarget.classList.remove('rack-unplaced--over')}
      onDrop={(e) => { e.currentTarget.classList.remove('rack-unplaced--over'); handleDropOnUnplaced(e); }}
    >
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">Pendientes sin U</h6>
        <span className="badge bg-light text-dark">{unplaced.length}</span>
      </div>
      <div className="rack-unplaced-hint">
        {touchMode ? 'Toca un equipo y luego la U donde va' : 'Arrastra aquí para descolocar'}
      </div>
      <div className="rack-unplaced-list">
        {unplaced.length === 0 ? (
          <div className="text-muted small">Todo está ubicado.</div>
        ) : (
          unplaced.map((el) => (
            <div
              key={`${el.source_kind || 'element'}:${el.id}`}
              className={`rack-chip${draggingId === `${el.source_kind || 'element'}:${el.id}` ? ' rack-chip--dragging' : ''}`}
              style={{borderLeft: `3px solid ${getElementColor(el.element_type || el.type)}`}}
              draggable
              onDragStart={(e) => handleDragStart(e, el)}
              onDragEnd={handleDragEnd}
              onClick={() => {
                setSelectedElement(el);
                // En la tablet, tocar un pendiente entra directo en «colocar».
                if (touchMode) beginMove(el);
              }}
            >
              <div className="fw-semibold small">{el.name || el.code}</div>
              <div className="d-flex justify-content-between small text-muted">
                <span>{el.element_type || el.type}</span>
                <span>{(el.size_u || el.size || 1)}U</span>
              </div>
            </div>
          ))
        )}
      </div>
    </aside>
  );

  return (
    <div
      className={`rack-viewer-container${isFullscreen ? ' rv--fullscreen' : ''}${touchMode ? ' rv--touch' : ''}`}
      ref={containerRef}
    >
      <div className="rv-header">
        <div className="rv-header-left">
          <button
            onClick={() => navigate(-1)}
            title="Volver al formulario del rack"
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--accent-l)', fontWeight: 500, fontSize: '0.82rem',
              padding: '4px 8px', borderRadius: 6,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(var(--accent-rgb), 0.12)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            ← Rack
          </button>
          <span style={{ color: 'var(--border)', margin: '0 2px' }}>|</span>
          <RectangleStackIcon className="rv-rack-glyph" aria-hidden="true" />
          <div className="rv-header-text">
            <div className="rv-title-row">
              <h1 className="rv-rack-name">{rack?.name}</h1>
              {rack?.code && <span className="rv-code-pill">{rack.code}</span>}
              {(rack?.location?.name || rack?.location_name) && (
                <span className="rv-location-pill">📍 {rack.location?.name || rack.location_name}</span>
              )}
            </div>
            {rack?.description && <p className="rv-rack-desc">{rack.description}</p>}
          </div>
        </div>
        <div className="rv-header-stats">
          {!isFullscreen && !touchMode && statChips}
          {saving && <span className="rv-saving-pill">💾 Guardando…</span>}
          <button
            type="button"
            className="rv-fs-btn"
            onClick={toggleTouchMode}
            title={touchMode ? 'Salir del modo tablet' : 'Modo tablet (dedo, botones grandes)'}
            aria-label={touchMode ? 'Salir del modo tablet' : 'Modo tablet'}
            aria-pressed={touchMode}
          >
            {touchMode ? '🖥️' : '📱'}
          </button>
          <button
            type="button"
            className="rv-fs-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? t('rack.exit_fullscreen') : t('rack.fullscreen')}
            aria-label={isFullscreen ? t('rack.exit_fullscreen') : t('rack.fullscreen')}
          >
            {isFullscreen ? '⤡' : '⛶'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {isDraft && (
        <div className="rv-design-banner">
          <span className="rv-design-banner-text">
            🎨 <strong>Borrador de diseño</strong> — no cuenta en el inventario ni consume numeración.
            {' '}Añade equipos, colócalos en las U y, cuando esté listo, pásalo a producción.
          </span>
          <button
            type="button"
            className="rv-design-banner-btn"
            onClick={() => setShowPromote(true)}
          >
            🚀 Pasar a producción
          </button>
        </div>
      )}

      {/* En modo tablet solo se ve UNA cara: los tres paneles (frontal, canal y
          trasero) no caben, y para inventariar delante del armario se trabaja
          de cara al frontal o a la trasera, no a las dos a la vez. */}
      {touchMode && (
        <div className="rv-face-tabs" role="tablist" aria-label="Cara del rack">
          <button
            type="button"
            role="tab"
            aria-selected={faceTab === 'front'}
            className={faceTab === 'front' ? 'is-on' : ''}
            onClick={() => setFaceTab('front')}
          >
            ◀ Frontal <em>{frontUsedU}/{heightU}U</em>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={faceTab === 'rear'}
            className={faceTab === 'rear' ? 'is-on' : ''}
            onClick={() => setFaceTab('rear')}
          >
            Trasero ▶ <em>{rearUsedU}/{heightU}U</em>
          </button>
          <span className="rv-face-hint">
            {unplaced.length ? `${unplaced.length} sin ubicar` : 'Todo ubicado'}
          </span>
        </div>
      )}

      {placing && (
        <div className="rv-place-bar" role="status">
          <span>
            📦 <strong>{placing.el.name || placing.el.code}</strong>
            {' '}· toca una U libre de la cara {faceTab === 'front' ? 'frontal' : 'trasera'}
            {' '}(termina en la U que toques)
          </span>
          <button type="button" onClick={cancelPlacing}>Cancelar</button>
        </div>
      )}

      <div className="rack-viewer-grid">
        {isFullscreen && !touchMode ? (
          <aside className="rv-rail">
            {addElementBar}
            {fillerBar}
            <div className="rv-rail-kpis">{statChips}</div>
            {unplacedPanel}
          </aside>
        ) : (
          !touchMode && unplacedPanel
        )}

        <div className={`rack-board${isCabLayer ? ' is-cabling' : ''}${touchMode ? ' is-touch' : ''}`}>

          {/* Capa activa: colocación o cableado. Ambas permiten mover equipos. */}
          <div className="rv-layer-bar">
            <div className="rv-layer-switch" role="group" aria-label="Capa del rack">
              <button
                type="button"
                className={!isCabLayer ? 'is-on' : ''}
                onClick={() => chooseLayer('layout')}
                title="Montar y mover equipos (sin cables)"
              >
                🧩 Colocación
              </button>
              <button
                type="button"
                className={isCabLayer ? 'is-on' : ''}
                onClick={() => chooseLayer('cabling')}
                title="Ver y conectar cables; el nombre de cada equipo sale al pasar el ratón"
              >
                🔌 Cableado
              </button>
            </div>
            {isCabLayer && (
              <span className="rv-layer-count">
                {cableGeoms.length} cables dibujados · {cabling.edges?.length || 0} conexiones
              </span>
            )}
            {touchMode && (
              <span className="rv-layer-count">
                Toca un equipo para verlo · toca un hueco para añadir
              </span>
            )}
          </div>

          {/* Conexión de dos puertos: barra en el propio rack, sin modales. */}
          {cabSel && !cabPair && (
            <div className="rv-connect-hint">
              <span>
                🔌 <strong>{cabSel.label}</strong> de {cabSel.nodeTitle}: haz clic en otro puerto libre del frontal.
              </span>
              <button type="button" onClick={() => setCabSel(null)}>cancelar</button>
            </div>
          )}
          {cabPair && (
            <div className="rv-connect-bar">
              <span className="rv-connect-title">
                🔌 {cabPair.a.nodeTitle} <strong>{cabPair.a.label}</strong>
                <em>↔</em>
                {cabPair.b.nodeTitle} <strong>{cabPair.b.label}</strong>
              </span>
              <select
                value={cabForm.cable_type}
                onChange={(e) => setCabForm((f) => ({ ...f, cable_type: e.target.value }))}
              >
                {CABLE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
              <select
                value={cabForm.cable_color}
                onChange={(e) => setCabForm((f) => ({ ...f, cable_color: e.target.value }))}
              >
                {CABLE_COLORS.map((color) => <option key={color.value} value={color.value}>{color.label}</option>)}
              </select>
              <button type="button" className="rv-connect-ok" onClick={doConnect} disabled={cabSaving}>
                {cabSaving ? 'Conectando…' : 'Conectar'}
              </button>
              <button type="button" onClick={() => { setCabPair(null); setCabError(null); }}>Cancelar</button>
              {cabError && <span className="rv-connect-error">{cabError}</span>}
            </div>
          )}

          <div className="rack-dual" ref={dualRef}>

            {/* En vista normal las barras van encima del rack; en pantalla
                completa viven en la barra lateral izquierda. En modo tablet se
                esconden: esas acciones están en la barra inferior, con dedo. */}
            {!isFullscreen && !touchMode && addElementBar}
            {!isFullscreen && fillerBar}

            {/* ──────── FRONTAL ──────── */}
            {(!touchMode || faceTab === 'front') && (
            <div className="rack-panel">
              <div className="rack-panel-label rack-panel-label--front">◀ Frontal</div>
              <div className="rack-columns" ref={frontColsRef} onScroll={handleColumnsScroll}>
                <div className="rack-scale" style={{ gridAutoRows: `${uPx}px` }}>
                  {slotNumbers.map((u) => (
                    <div key={u} className="rack-scale-cell">U{u}</div>
                  ))}
                </div>
                <div className="rack-canvas" ref={frontCanvasRef} style={{ gridTemplateRows: `repeat(${heightU}, ${uPx}px)` }}>
                  {slotNumbers.map((u) => {
                    const isFrontBlocked = fullDepthRearUs.has(u)
                      && !placedFront.some(el => {
                        const startU = el.position_u || el.position;
                        const sizeU  = el.size_u || el.size || 1;
                        return startU && u >= startU && u <= startU + sizeU - 1;
                      })
                      && !fillersFront.some(f => f.position_u === u);
                    return (
                    <div
                      key={u}
                      className={`rack-slot${dropTarget?.u === u && dropTarget?.side === 'front' ? ' rack-slot--over' : ''}${isFrontBlocked ? ' rack-slot--blocked' : ''}${touchMode && placing && canPlaceAt(u, 'front', placing.el) ? ' rack-slot--target' : ''}`}
                      data-u={u}
                      style={{
                        position: 'relative',
                        ...(fillerMode ? { cursor: isFrontBlocked ? 'not-allowed' : 'crosshair' } : undefined),
                      }}
                      onDragOver={(e) => isFrontBlocked ? e.preventDefault() : allowDrop(e, u, 'front')}
                      onDragLeave={() => setDropTarget(null)}
                      onDrop={(e) => handleDropOnU(e, u, 'front')}
                      onClick={() => handleSlotClick(u, 'front')}
                    >
                      {isFrontBlocked && (
                        <div style={{
                          position: 'absolute', inset: 0,
                          background: 'repeating-linear-gradient(45deg,#e2e8f0,#e2e8f0 5px,#f8fafc 5px,#f8fafc 10px)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.58rem', color: '#94a3b8', fontStyle: 'italic', letterSpacing: 0.3,
                          zIndex: 1, pointerEvents: 'none',
                        }}>
                          ↔ full depth (trasero)
                        </div>
                      )}
                      {placedFront.filter((el) => getElementTopU(el) === u).map((el) => (
                        <div
                          key={`${el.source_kind || 'element'}:${el.id}`}
                          id={`rack-el-${el.id}`}
                          className={`rack-item rack-item--${el.element_type || el.type}${draggingId === `${el.source_kind || 'element'}:${el.id}` ? ' rack-item--dragging' : ''}${el.id === hlId ? ' rack-item--highlight' : ''}`}
                          style={{ height: `${(el.size_u || el.size || 1) * uPx + ((el.size_u || el.size || 1) - 1) * 2}px`, background: getElementColor(el.element_type || el.type) }}
                          draggable
                          onDragStart={(e) => handleDragStart(e, el)}
                          onDragEnd={handleDragEnd}
                          onClick={(e) => { e.stopPropagation(); selectElement(el); }}
                          onDoubleClick={() => {
                            if (el.element_type === 'panel' || el.element_type === 'switch') {
                              setPortModalElement(el);
                            }
                          }}
                          title={`${el.name || el.code} · ${el.element_type || el.type}${el.ports_summary ? ` · ${el.ports_summary.connected}/${el.ports_summary.total} puertos` : ''} · ${formatRange(el.position_u || el.position, el.size_u || el.size || 1)}`}
                        >
                          <RackItemContent el={el} sizeU={el.size_u || el.size || 1} />
                          {isCabLayer && renderPorts(el)}
                        </div>
                      ))}
                      {fillersFront.filter(f => f.position_u === u).map(f => (
                        <div
                          key={`filler-front-${u}`}
                          className="rack-filler-item"
                          style={{ background: FILLER_META[f.filler_type]?.color || '#94a3b8' }}
                          onClick={(e) => { e.stopPropagation(); handleFillerClick(f); }}
                          title={`${FILLER_META[f.filler_type]?.label || f.filler_type} · Clic para quitar`}
                        >
                          {FILLER_META[f.filler_type]?.icon} {FILLER_META[f.filler_type]?.label}
                        </div>
                      ))}
                      {touchMode && placing && canPlaceAt(u, 'front', placing.el) && (
                        <span className="rack-slot-plus" aria-hidden="true">＋</span>
                      )}
                    </div>
                    );})}
                </div>
              </div>
            </div>
            )}

            {/* ──────── COLUMNA DE CABLEADO ──────── */}
            {!touchMode && (
            <div className="rack-panel rack-panel--duct">
              <div className="rack-panel-label rack-panel-label--duct">🔌 Cableado</div>
              <div className="rack-columns rack-columns--duct" ref={ductColsRef} onScroll={handleColumnsScroll}>
                <div className="rack-canvas rack-canvas--duct" ref={ductCanvasRef} style={{ gridTemplateRows: `repeat(${heightU}, ${uPx}px)` }} />
              </div>
            </div>
            )}

            {/* ──────── TRASERO ──────── */}
            {(!touchMode || faceTab === 'rear') && (
            <div className="rack-panel rack-panel--rear">
              <div className="rack-panel-label rack-panel-label--rear">Trasero ▶</div>
              <div className="rack-columns" ref={rearColsRef} onScroll={handleColumnsScroll}>
                <div className="rack-scale" style={{ gridAutoRows: `${uPx}px` }}>
                  {slotNumbers.map((u) => (
                    <div key={u} className="rack-scale-cell">U{u}</div>
                  ))}
                </div>
                <div className="rack-canvas" style={{ gridTemplateRows: `repeat(${heightU}, ${uPx}px)` }}>
                  {slotNumbers.map((u) => {
                    const isRearBlocked = fullDepthFrontUs.has(u)
                      && !placedRear.some(el => getRange(el.position_u || el.position, el.size_u || el.size || 1)?.top === u)
                      && !fillersRear.some(f => f.position_u === u);
                    return (
                    <div
                      key={`rear-${u}`}
                      className={`rack-slot${dropTarget?.u === u && dropTarget?.side === 'rear' ? ' rack-slot--over' : ''}${isRearBlocked ? ' rack-slot--blocked' : ''}${touchMode && placing && canPlaceAt(u, 'rear', placing.el) ? ' rack-slot--target' : ''}`}
                      data-u={u}
                      style={{
                        position: 'relative',
                        ...(fillerMode ? { cursor: isRearBlocked ? 'not-allowed' : 'crosshair' } : undefined),
                      }}
                      onDragOver={(e) => isRearBlocked ? e.preventDefault() : allowDrop(e, u, 'rear')}
                      onDragLeave={() => setDropTarget(null)}
                      onDrop={(e) => handleDropOnU(e, u, 'rear')}
                      onClick={() => handleSlotClick(u, 'rear')}
                    >
                      {isRearBlocked && (
                        <div style={{
                          position: 'absolute', inset: 0,
                          background: 'repeating-linear-gradient(45deg,#e2e8f0,#e2e8f0 5px,#f8fafc 5px,#f8fafc 10px)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.58rem', color: '#94a3b8', fontStyle: 'italic', letterSpacing: 0.3,
                          zIndex: 1, pointerEvents: 'none',
                        }}>
                          ↔ full depth
                        </div>
                      )}
                      {placedRear.filter((el) => getElementTopU(el) === u).map((el) => (
                        <div
                          key={`${el.source_kind || 'element'}:${el.id}`}
                          id={`rack-el-${el.id}`}
                          className={`rack-item rack-item--${el.element_type || el.type}${draggingId === `${el.source_kind || 'element'}:${el.id}` ? ' rack-item--dragging' : ''}${el.id === hlId ? ' rack-item--highlight' : ''}`}
                          style={{ height: `${(el.size_u || el.size || 1) * uPx + ((el.size_u || el.size || 1) - 1) * 2}px`, background: getElementColor(el.element_type || el.type) }}
                          draggable
                          onDragStart={(e) => handleDragStart(e, el)}
                          onDragEnd={handleDragEnd}
                          onClick={(e) => { e.stopPropagation(); selectElement(el); }}
                          onDoubleClick={() => {
                            if (el.element_type === 'panel' || el.element_type === 'switch') {
                              setPortModalElement(el);
                            }
                          }}
                          title={`${el.name || el.code} · ${el.element_type || el.type}${el.ports_summary ? ` · ${el.ports_summary.connected}/${el.ports_summary.total} puertos` : ''} · ${formatRange(el.position_u || el.position, el.size_u || el.size || 1)}`}
                        >
                          <RackItemContent el={el} sizeU={el.size_u || el.size || 1} />
                        </div>
                      ))}
                      {fillersRear.filter(f => f.position_u === u).map(f => (
                        <div
                          key={`filler-rear-${u}`}
                          className="rack-filler-item"
                          style={{ background: FILLER_META[f.filler_type]?.color || '#94a3b8' }}
                          onClick={(e) => { e.stopPropagation(); handleFillerClick(f); }}
                          title={`${FILLER_META[f.filler_type]?.label || f.filler_type} · Clic para quitar`}
                        >
                          {FILLER_META[f.filler_type]?.icon} {FILLER_META[f.filler_type]?.label}
                        </div>
                      ))}
                      {touchMode && placing && canPlaceAt(u, 'rear', placing.el) && (
                        <span className="rack-slot-plus" aria-hidden="true">＋</span>
                      )}
                    </div>
                    );})}
                </div>
              </div>
            </div>
            )}

            {/* Capa ÚNICA de cables sobre el tablero: se desplaza con el scroll
                mediante un translate, así los cables siempre acompañan a las U. */}
            {isCabLayer && (!touchMode || faceTab === 'front') && (
              <svg
                className="rv-cable-overlay"
                aria-hidden="true"
                style={{ clipPath: `inset(${cabLayout.canvasTop}px 0 0 0)` }}
              >
                <g transform={`translate(0, ${cabLayout.canvasTop - scrollY})`}>
                  {cableGeoms.map((cable) => (
                    <path
                      key={cable.key}
                      d={cable.d}
                      stroke={cable.color}
                      strokeDasharray={cable.dashed ? '6 4' : undefined}
                      className={`rv-cable${cable.kind === 'backbone' ? ' is-backbone' : ''}${hot.edges.has(cable.key) ? ' is-hot' : ''}${hot.hasPath && !hot.edges.has(cable.key) ? ' is-dim' : ''}`}
                    />
                  ))}
                </g>
              </svg>
            )}

          </div>

          {/* Tooltip del puerto: qué es y con qué está conectado. */}
          {isCabLayer && hoverTip && hoverPortKey && (
            <div className="rv-cable-tip" style={{ left: hoverTip.x, top: hoverTip.y }}>
              <div className="rv-cable-tip-head">
                {portIndex.get(hoverPortKey)?.node.title}
                <span>{portIndex.get(hoverPortKey)?.port.label}</span>
              </div>
              {hot.links.length ? hot.links.map((link, i) => (
                <div key={`tip-${i}`} className="rv-cable-tip-row">
                  → {link.title}
                  {link.location && (
                    <span className="rv-cable-tip-loc">📍 {link.location}</span>
                  )}
                  {(link.cable || link.color) && (
                    <em>{[link.cable, link.color].filter(Boolean).join(' · ')}</em>
                  )}
                </div>
              )) : (
                <div className="rv-cable-tip-row is-muted">Libre · sin cable</div>
              )}
            </div>
          )}
        </div>

        {touchMode && sheetOpen && (
          <div className="rv-sheet-backdrop" onClick={() => setSheetOpen(false)} />
        )}

        <aside className={`rack-side${touchMode ? ' rv-sheet-side' : ''}${touchMode && sheetOpen ? ' is-open' : ''}`}>

          {touchMode && (
            <div className="rv-sheet-head">
              <span>
                🗄️ {selectedElement ? (selectedElement.name || selectedElement.code) : rack?.name}
              </span>
              <button
                type="button"
                className="rv-sheet-close"
                onClick={() => setSheetOpen(false)}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
          )}

          {touchMode && selectedElement && (
            <div className="rv-touch-actions">
              <button type="button" className="is-primary" onClick={() => beginMove(selectedElement)}>
                📦 Mover a otra U
              </button>
              <button type="button" className="is-danger" onClick={() => unplaceElement(selectedElement)} disabled={saving}>
                ↩ Quitar del rack
              </button>
              {(selectedElement.element_type === 'panel' || selectedElement.element_type === 'switch') && (
                <button type="button" onClick={() => setPortModalElement(selectedElement)}>
                  🔌 Puertos
                </button>
              )}
              {(selectedElement.element_type === 'server' || selectedElement.element_type === 'storage') && (
                <button type="button" onClick={() => setComponentsElement(selectedElement)}>
                  🧩 Componentes
                </button>
              )}
              {selectedElement.install_state && selectedElement.install_state !== 'verified' && (
                <button type="button" onClick={() => verifyElement(selectedElement)} disabled={verifying}>
                  {verifying ? 'Validando…' : '✔ Validar'}
                </button>
              )}
            </div>
          )}

          {touchMode && !selectedElement && (
            <div className="rv-inline-help" style={{ marginBottom: 10 }}>
              Toca un equipo del rack para ver su ficha y moverlo.
            </div>
          )}

          {/* ── Leyenda ── */}
          <div className="rv-legend">
            <div className="rv-legend-item"><span className="rv-dot rv-dot--server"/>Servidor</div>
            <div className="rv-legend-item"><span className="rv-dot" style={{ background: '#8b5cf6' }}/>Chasis</div>
            <div className="rv-legend-item"><span className="rv-dot rv-dot--switch"/>Switch</div>
            <div className="rv-legend-item"><span className="rv-dot rv-dot--panel"/>Panel</div>
            <div className="rv-legend-item"><span className="rv-dot rv-dot--pdu"/>PDU</div>
            <div className="rv-legend-item"><span className="rv-dot" style={{ background: '#f59e0b' }}/>SAI</div>
          </div>

          {/* ── Capacidad ── */}
          <div className="rv-occupancy">
            <div className="rv-spec-header">Capacidad por cara</div>
            {renderOccupancyBar('Frontal', frontUsedU, frontFreeU, 'front')}
            {renderOccupancyBar('Trasera', rearUsedU, rearFreeU, 'rear')}
            <div className="rv-occupancy-help">
              La trasera incluye el bloqueo de equipos marcados como profundidad completa.
            </div>
          </div>

          {/* ── Alimentación eléctrica ── */}
          {powerSummary && (
            <div className="spec-group rv-power-panel">
              <div
                className="rv-spec-header"
                style={{ cursor: 'pointer', userSelect: 'none', display: 'flex', justifyContent: 'space-between' }}
                onClick={() => setPowerOpen(p => !p)}
              >
                <span>⚡ Alimentación</span>
                <span style={{ fontSize: 10 }}>{powerOpen ? '▲' : '▼'}</span>
              </div>
              {powerOpen && (
                <>
                  <div className="rv-power-summary-grid">
                    <div className="rv-power-summary-card">
                      <span className="rv-power-summary-value">{powerPdus.length}</span>
                      <span className="rv-power-summary-label">PDUs</span>
                    </div>
                    <div className="rv-power-summary-card">
                      <span className="rv-power-summary-value">{powerOutletsUsed}/{powerOutletsTotal || 0}</span>
                      <span className="rv-power-summary-label">Tomas usadas</span>
                    </div>
                    <div className="rv-power-summary-card">
                      <span className="rv-power-summary-value">{redundantPowerDevices}/{powerDevices.length || 0}</span>
                      <span className="rv-power-summary-label">Equipos redundantes</span>
                    </div>
                  </div>

                  {powerPdus.map(pdu => {
                    const pct = pdu.outlets_total > 0 ? Math.round((pdu.outlets_used / pdu.outlets_total) * 100) : 0;
                    const barColor = pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#22c55e';
                    return (
                      <div key={pdu.id} className="rv-power-pdu-row">
                        <div className="rv-power-pdu-head">
                          <span className="rv-power-pdu-name">{pdu.name}</span>
                          <span className="rv-power-pdu-metric">{pdu.outlets_used}/{pdu.outlets_total || 0}</span>
                        </div>
                        <div className="rv-power-track">
                          <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 999, transition: 'width .3s' }} />
                        </div>
                      </div>
                    );
                  })}
                  {powerPdus.length === 0 && (
                    <div className="rv-empty-note">No hay PDUs en este rack.</div>
                  )}
                  {powerDevices.length > 0 && (
                    <div className="rv-power-devices">
                      <div className="rv-power-devices-title">Dispositivos</div>
                      {powerDevices.map((d, i) => {
                        const meta = getPowerStateMeta(d);
                        return (
                          /* La clave no puede salir solo de consumer_type/consumer_id:
                             las tomas sin consumidor asignado dejan `undefined-undefined`
                             repetido y React avisa en cada render. */
                          <div key={`${d.consumer_type || 'toma'}-${d.consumer_id ?? `idx${i}`}`} className="rv-power-device-row">
                            <span className="rv-power-device-name">{d.consumer_name}</span>
                            <span className={`rv-power-state rv-power-state--${meta.tone}`}>{meta.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {powerDevices.length === 0 && (
                    <div className="rv-empty-note">No hay equipos vinculados a PDUs.</div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="spec-group">
            <div className="rv-spec-header">Rack</div>
            <div className="spec"><span>{t('rack.code')}:</span><span>{rack?.code}</span></div>
            <div className="spec"><span>{t('rack.height')}:</span><span>{rack?.height_u}U</span></div>
            <div className="spec"><span>{t('rack.width')}:</span><span>{rack?.width_mm}mm</span></div>
            <div className="spec"><span>{t('rack.depth')}:</span><span>{rack?.depth_mm}mm</span></div>
            <div className="spec"><span>Libre frontal:</span><span>{frontFreeU}U</span></div>
            <div className="spec"><span>Libre trasera:</span><span>{rearFreeU}U</span></div>
          </div>
          {selectedElement && (
            <div className="spec-group">
              <div className="rv-spec-header">Elemento seleccionado</div>
              <div className="spec"><span>{t('element.name')}:</span><span>{selectedElement.name || selectedElement.code}</span></div>
              <div className="spec"><span>{t('element.type')}:</span><span>{selectedElement.element_type || selectedElement.type}</span></div>
              <div className="spec"><span>{t('element.position')}:</span><span>{formatRange(selectedElement.position_u || selectedElement.position, selectedElement.size_u || selectedElement.size || 1)}</span></div>
              <div className="spec"><span>U inferior:</span><span>U{selectedElement.position_u || selectedElement.position || '—'}</span></div>
              <div className="spec"><span>U superior:</span><span>U{getRange(selectedElement.position_u || selectedElement.position, selectedElement.size_u || selectedElement.size || 1)?.top || '—'}</span></div>
              <div className="spec"><span>Cara:</span><span>{selectedElement.position_front !== false ? '\u25c4 Frontal' : 'Trasero \u25ba'}</span></div>
              <div className="spec"><span>Altura:</span><span>{selectedElement.size_u || selectedElement.size || 1}U</span></div>

              {selectedElement.install_state && (
                <>
                  <div className="spec">
                    <span>Montaje:</span>
                    <span style={{ fontWeight: 600 }}>
                      {INSTALL_LABEL[selectedElement.install_state] || selectedElement.install_state}
                    </span>
                  </div>

                  {readinessById.get(selectedElement.id)?.missing?.length > 0 && (
                    <div className="rv-inline-help" style={{ color: '#b45309' }}>
                      Faltan:{' '}
                      {readinessById.get(selectedElement.id).missing
                        .map((f) => MISSING_LABEL[f] || f)
                        .join(', ')}
                    </div>
                  )}

                  {selectedElement.install_state !== 'verified' && (
                    <button
                      className="btn btn-sm btn-outline-success mt-2 w-100"
                      disabled={verifying}
                      onClick={() => verifyElement(selectedElement)}
                    >
                      {verifying ? 'Validando…' : '✔ Dar por validado'}
                    </button>
                  )}
                </>
              )}

              {/* Full-depth toggle (not for chassis items) */}
              {(selectedElement.source_kind !== 'chassis') && (
                <div className="spec spec--stacked" style={{ alignItems: 'flex-start' }}>
                  <span style={{ fontSize: '0.78rem' }}>Profundidad completa:</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: patchingElement ? 'not-allowed' : 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(selectedElement.full_depth)}
                      disabled={patchingElement}
                      onChange={() => {
                        const updated = { ...selectedElement, full_depth: !selectedElement.full_depth };
                        setSelectedElement(updated);
                        handleElementPatch(selectedElement.id, { full_depth: !selectedElement.full_depth });
                      }}
                    />
                    <span style={{ fontSize: '0.75rem', color: '#475569' }}>
                      {selectedElement.full_depth ? 'S\u00ed \u2194 ocupa frente+trasero' : 'No'}
                    </span>
                  </label>
                  <span className="rv-inline-help">Disponible para cualquier altura. Si se activa, bloquea las mismas U de la cara trasera.</span>
                </div>
              )}

              {/* PDU strip info (read-only here, edit via PDU outlet view) */}
              {selectedElement.element_type === 'pdu' && (
                <>
                  {selectedElement.pdu_phase && (
                    <div className="spec">
                      <span>Fase:</span>
                      <span style={{ fontWeight: 600, color: '#1d4ed8' }}>{selectedElement.pdu_phase}</span>
                    </div>
                  )}
                  {selectedElement.pdu_amperage != null && (
                    <div className="spec">
                      <span>Amperios:</span>
                      <span style={{ fontWeight: 600 }}>{selectedElement.pdu_amperage} A</span>
                    </div>
                  )}
                  {selectedElement.parent_pdu_id && (
                    <div className="spec">
                      <span>PDU principal:</span>
                      <span style={{ color: '#7c3aed' }}>#{selectedElement.parent_pdu_id}</span>
                    </div>
                  )}
                </>
              )}

              {(selectedElement.element_type === 'server' || selectedElement.element_type === 'storage') && (
                <button
                  className="btn btn-sm btn-outline-primary mt-2 w-100"
                  onClick={() => setComponentsElement(selectedElement)}
                >
                  Componentes (NICs / HBAs)
                </button>
              )}

              {(selectedElement.element_type === 'panel' || selectedElement.element_type === 'switch') && (
                <button
                  className="btn btn-sm btn-outline-primary mt-2 w-100"
                  onClick={() => setPortModalElement(selectedElement)}
                >
                  Abrir mapa de puertos
                </button>
              )}
            </div>
          )}
          {selectedElement && (
            <div className="spec-group">
              <div className="rv-spec-header">Conexiones activas</div>
              {loadingConnections ? (
                <div style={{ fontSize: '0.85rem', color: '#6c757d', fontStyle: 'italic' }}>Cargando...</div>
              ) : elementConnections.length === 0 ? (
                <div style={{ fontSize: '0.85rem', color: '#6c757d', fontStyle: 'italic' }}>Sin conexiones</div>
              ) : (
                <ul style={{ fontSize: '0.85rem', margin: '0.5rem 0', paddingLeft: '1.2rem' }}>
                  {elementConnections.map((conn, idx) => (
                    <li key={idx} style={{ marginBottom: '0.3rem' }}>
                      <strong>P{conn.source_port_number}</strong> →{' '}
                      {conn.destination_element_name ? (
                        <span>{conn.destination_element_name} P{conn.destination_port_number}</span>
                      ) : conn.wall_outlet_code ? (
                        <span>Toma {conn.wall_outlet_code}</span>
                      ) : (
                        <span style={{ color: '#999' }}>destino desconocido</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </aside>
      </div>

      {touchMode && (
        <div className="rv-touch-dock">
          <button type="button" onClick={() => setAddSheet({ u: null, side: faceTab, mode: 'device' })}>
            ＋<span>Añadir equipo</span>
          </button>
          <button type="button" onClick={() => setUnplacedOpen(true)}>
            📦<span>Sin ubicar{unplaced.length ? ` (${unplaced.length})` : ''}</span>
          </button>
          <button
            type="button"
            className={fillerMode ? 'is-on' : ''}
            onClick={() => setAddSheet({ u: null, side: faceTab, mode: 'filler' })}
          >
            ▬<span>Accesorio</span>
          </button>
          <button type="button" onClick={toggleFullscreen}>
            {isFullscreen ? '⤡' : '⛶'}<span>Pantalla</span>
          </button>
        </div>
      )}

      {/* Hoja: pendientes sin U (en la tablet no cabe el panel lateral). */}
      {touchMode && unplacedOpen && (
        <div className="rv-sheet-backdrop" onClick={() => setUnplacedOpen(false)}>
          <div className="rv-sheet rv-sheet--unplaced" onClick={(e) => e.stopPropagation()}>
            <div className="rv-sheet-head">
              <span>📦 Pendientes sin U</span>
              <button type="button" className="rv-sheet-close" onClick={() => setUnplacedOpen(false)} aria-label="Cerrar">✕</button>
            </div>
            {unplacedPanel}
          </div>
        </div>
      )}

      {/* Hoja: añadir equipo o accesorio en la cara que estás viendo. */}
      {touchMode && addSheet && (
        <div className="rv-sheet-backdrop" onClick={() => setAddSheet(null)}>
          <div className="rv-sheet rv-sheet--add" onClick={(e) => e.stopPropagation()}>
            <div className="rv-sheet-head">
              <span>
                {addSheet.u ? `U${addSheet.u} · ` : ''}
                {addSheet.side === 'rear' ? 'Trasera' : 'Frontal'}
              </span>
              <button type="button" className="rv-sheet-close" onClick={() => setAddSheet(null)} aria-label="Cerrar">✕</button>
            </div>
            <div className="rv-sheet-body">
              {addSheet.u && selectedElement && (
                <button
                  type="button"
                  className="rv-big-btn is-primary"
                  onClick={() => {
                    const target = selectedElement;
                    const { u, side } = addSheet;
                    setAddSheet(null);
                    moveElementTo(target, u, side);
                  }}
                >
                  📦 Mover «{selectedElement.name || selectedElement.code}» aquí
                </button>
              )}

              {addSheet.mode === 'filler' ? (
                <>
                  <div className="rv-sheet-sub">Accesorio para los huecos libres</div>
                  <div className="rv-type-grid">
                    {Object.entries(FILLER_META).map(([type, meta]) => (
                      <button
                        key={type}
                        type="button"
                        className={fillerMode === type ? 'is-on' : ''}
                        onClick={() => { setFillerMode(type); setPlacing(null); setAddSheet(null); }}
                      >
                        <span aria-hidden="true">{meta.icon}</span>{meta.label}
                      </button>
                    ))}
                  </div>
                  <p className="rv-sheet-note">
                    Después toca los huecos libres del rack. Un toque en un relleno lo quita.
                  </p>
                </>
              ) : (
                <>
                  <div className="rv-sheet-sub">Equipo nuevo</div>
                  <div className="rv-type-grid">
                    {[
                      { type: 'switch', label: 'Switch', icon: '🔀' },
                      { type: 'panel', label: 'Patch panel', icon: '🔗' },
                      { type: 'pdu', label: 'PDU', icon: '⚡' },
                    ].map((item) => (
                      <button
                        key={item.type}
                        type="button"
                        onClick={() => {
                          setQuickCreateAt({ u: addSheet.u, side: addSheet.side });
                          setQuickCreateType(item.type);
                          setAddSheet(null);
                        }}
                      >
                        <span aria-hidden="true">{item.icon}</span>{item.label}
                      </button>
                    ))}
                  </div>
                  <p className="rv-sheet-note">
                    El nombre arranca en el siguiente de la nomenclatura y el rack y la ubicación se heredan.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {portModalElement && (
        <PortMapModal
          element={portModalElement}
          rackElements={elements}
          companyId={companyId}
          onClose={() => setPortModalElement(null)}
          onUpdated={fetchRack}
        />
      )}
      {componentsElement && (
        <ComponentsModal
          element={componentsElement}
          companyId={companyId}
          onClose={() => setComponentsElement(null)}
        />
      )}
      {quickCreateType && (
        <RackElementQuickCreate
          elementType={quickCreateType}
          companyId={companyId}
          rackId={rackIdNum}
          locationId={rackLocationId}
          rackName={rack?.name || rack?.code}
          locationName={rack?.location?.name || rack?.location_name}
          heightU={heightU}
          occupied={{ front: frontOccupiedUs, rear: rearOccupiedUs }}
          // Hueco tocado en modo tablet (U superior + cara).
          initialU={quickCreateAt?.u ?? null}
          initialSide={quickCreateAt?.side ?? null}
          designMode={isDraft}
          onClose={() => { setQuickCreateType(null); setQuickCreateAt(null); }}
          onCreated={handleQuickCreated}
        />
      )}
      {showPromote && (
        <PromoteRackDialog
          rack={rack}
          companyId={companyId}
          onClose={() => setShowPromote(false)}
          onPromoted={() => { setShowPromote(false); fetchRack(); }}
        />
      )}
    </div>
  );
}

export default RackViewer;
